import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import useSWR from 'swr'
import { api } from '../services/api'
import { Badge, Banner, Button, CompetitorSelect, CupTeamSelect, ImageManagement, Panel, SeasonSelect } from '../components'
import type { CupTeamOption } from '../components'
import { Competitor, Season } from '../types/forms'
import { cupTeamName, roundLabel } from '../utils/cupTeams'
import { convertPTInputToUTC } from '../utils/timezone'
import pageStyles from '../styles/page.module.scss'
import formStyles from '../styles/forms.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import styles from './CupTeams.module.scss'

interface CupTeam {
  tc_cup_team_id: string
  ext_competitor_id: string | null
  tc_name: string | null
  ext_name: string | null
  tc_abbreviation: string | null
  ext_abbreviation: string | null
  country_code: string | null
  image_url: string | null
  image_source: 'logo' | 'flag' | null
}

interface CupTeamSeason {
  tc_season_id: string
  season_name: string | null
  competition_name: string | null
  start_date: string | null
  end_date: string | null
  roster_count: number
  tie_count: number
  added_by_editor: boolean
}

interface RosterEntry {
  player_id: string
  name: string | null
  country_code: string | null
  created_by: string
  created_at: string
  has_rubbers: boolean
}

interface TeamTie {
  tc_match_id: string
  start_time: string | null
  status: string | null
  round: string | null
  group_name: string | null
  opponent_id: string | null
  opponent_name: string | null
  team_is_home: boolean
  home_tie_score: number | null
  away_tie_score: number | null
  won: boolean | null
}

const addedByLabel = (createdBy: string) => (createdBy.includes('@') ? createdBy : 'From matches')

const CupTeamDetail: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>()
  const [seasonId, setSeasonId] = useState<string | null>(null)
  const [nameOverride, setNameOverride] = useState('')
  const [abbrOverride, setAbbrOverride] = useState('')
  const [countryOverride, setCountryOverride] = useState('')
  const [playerToAdd, setPlayerToAdd] = useState<Competitor | null>(null)
  const [seasonToEnter, setSeasonToEnter] = useState<Season | null>(null)
  const [showNewTie, setShowNewTie] = useState(false)
  const [tieOpponent, setTieOpponent] = useState<CupTeamOption | null>(null)
  const [tieSide, setTieSide] = useState<'home' | 'away'>('home')
  const [tieStart, setTieStart] = useState('')
  const [tieRound, setTieRound] = useState('')
  const navigate = useNavigate()
  const [message, setMessage] = useState<{ variant: 'success' | 'error'; text: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const { data, error, mutate } = useSWR(teamId ? ['cup-team', teamId] : null, () =>
    api.get<{ cup_team: CupTeam; seasons: CupTeamSeason[] }>(`/cup-teams/${teamId}`),
  )
  const team = data?.cup_team
  const seasons = useMemo(() => data?.seasons || [], [data])

  useEffect(() => {
    if (team) {
      setNameOverride(team.tc_name || '')
      setAbbrOverride(team.tc_abbreviation || '')
    }
  }, [team])

  useEffect(() => {
    if (!seasonId && seasons.length > 0) setSeasonId(seasons[0].tc_season_id)
  }, [seasons, seasonId])

  const { data: seasonData, mutate: mutateSeason } = useSWR(
    teamId && seasonId ? ['cup-team-season', teamId, seasonId] : null,
    () => api.get<{ roster: RosterEntry[]; ties: TeamTie[] }>(`/cup-teams/${teamId}/seasons/${seasonId}`),
  )
  const roster = seasonData?.roster || []
  const ties = seasonData?.ties || []
  const selectedSeason = seasons.find((s) => s.tc_season_id === seasonId)

  const run = async (action: () => Promise<unknown>, success: string) => {
    setBusy(true)
    setMessage(null)
    try {
      await action()
      setMessage({ variant: 'success', text: success })
      await Promise.all([mutate(), mutateSeason()])
    } catch (err) {
      setMessage({ variant: 'error', text: (err as Error).message })
    } finally {
      setBusy(false)
    }
  }

  const saveDetails = () =>
    run(
      () =>
        api.patch(`/cup-teams/${teamId}`, {
          tc_name: nameOverride,
          tc_abbreviation: abbrOverride,
          ...(countryOverride ? { tc_country_code: countryOverride.toUpperCase() } : {}),
        }),
      'Team details saved.',
    )

  const addPlayer = () =>
    playerToAdd &&
    run(async () => {
      await api.put(`/cup-teams/${teamId}/seasons/${seasonId}/roster/${playerToAdd.tc_id}`)
      setPlayerToAdd(null)
    }, `${playerToAdd.name} added to the roster.`)

  const enterSeason = () =>
    seasonToEnter &&
    run(async () => {
      await api.put(`/cup-teams/${teamId}/seasons/${seasonToEnter.tc_id}`)
      setSeasonId(seasonToEnter.tc_id)
      setSeasonToEnter(null)
    }, `Added to ${seasonToEnter.name}.`)

  const leaveSeason = (s: CupTeamSeason) =>
    run(async () => {
      await api.delete(`/cup-teams/${teamId}/seasons/${s.tc_season_id}`)
      if (s.tc_season_id === seasonId) setSeasonId(null)
    }, `Removed from ${s.season_name}.`)

  const createTie = async () => {
    setBusy(true)
    setMessage(null)
    try {
      const created = await api.post<{ tie_id: string }>(`/cup-teams/${teamId}/seasons/${seasonId}/ties`, {
        opponent_id: tieOpponent?.tc_cup_team_id,
        team_is_home: tieSide === 'home',
        tc_start_time: tieStart ? convertPTInputToUTC(tieStart) : null,
        tc_round: tieRound.trim() || null,
      })
      navigate(`/ties/${created.tie_id}`)
    } catch (err) {
      setMessage({ variant: 'error', text: (err as Error).message })
      setBusy(false)
    }
  }

  const removePlayer = (entry: RosterEntry) =>
    run(() => api.delete(`/cup-teams/${teamId}/seasons/${seasonId}/roster/${entry.player_id}`), `${entry.name} removed from the roster.`)

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading cup team</h2>
          <p>{(error as Error).message}</p>
        </div>
      </div>
    )
  }
  if (!team) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.loading}>
          <h3>Loading cup team...</h3>
        </div>
      </div>
    )
  }

  const teamScore = (t: TeamTie) => {
    const ours = t.team_is_home ? t.home_tie_score : t.away_tie_score
    const theirs = t.team_is_home ? t.away_tie_score : t.home_tie_score
    return ours === null || theirs === null ? '—' : `${ours}–${theirs}`
  }

  return (
    <div>
      <div className={pageStyles.pageHeader}>
        <h1 className={styles.teamTitle}>
          {team.image_url && <img className={styles.teamImage} src={team.image_url} alt="" />}
          {cupTeamName(team)}
        </h1>
        <p>
          {team.ext_competitor_id ? (
            <code className="id-code">{team.ext_competitor_id}</code>
          ) : (
            <Badge variant="secondary">Editorial team</Badge>
          )}{' '}
          {team.country_code && <Badge variant="primary">{team.country_code}</Badge>}{' '}
        </p>
      </div>

      {message && <Banner variant={message.variant} message={message.text} onDismiss={() => setMessage(null)} />}

      <div className={styles.teamLayout}>
        <Panel margin="none">
          <Panel.Header>
            <h2>Team details</h2>
          </Panel.Header>
          <div className={styles.fieldGrid}>
            <div className={formStyles.fieldGroup}>
              <label htmlFor="cup-team-name">Name</label>
              <input
                id="cup-team-name"
                type="text"
                value={nameOverride}
                onChange={(e) => setNameOverride(e.target.value)}
                placeholder={team.ext_name || 'Team name'}
                disabled={busy}
              />
            </div>
            <div className={formStyles.fieldGroup}>
              <label htmlFor="cup-team-abbr">Abbreviation</label>
              <input
                id="cup-team-abbr"
                type="text"
                value={abbrOverride}
                onChange={(e) => setAbbrOverride(e.target.value)}
                placeholder={team.ext_abbreviation || ''}
                disabled={busy}
              />
            </div>
            <div className={formStyles.fieldGroup}>
              <label htmlFor="cup-team-country">Country code (flag)</label>
              <input
                id="cup-team-country"
                type="text"
                value={countryOverride}
                onChange={(e) => setCountryOverride(e.target.value)}
                placeholder={team.country_code || 'ESP'}
                disabled={busy}
              />
            </div>
            {team.ext_competitor_id && <p className={styles.muted}>Empty fields use the Sportradar value shown in grey.</p>}
          </div>
          <div className={styles.actionButtons}>
            <Button onClick={saveDetails} disabled={busy}>Save details</Button>
          </div>
        </Panel>

        <Panel margin="none">
          <Panel.Header>
            <h2>Image</h2>
          </Panel.Header>
          <div className={styles.imageSummary}>
            {team.image_url ? (
              <img className={styles.imagePreview} src={team.image_url} alt={`${cupTeamName(team)} image`} />
            ) : (
              <div className={styles.imageMissing}>No image</div>
            )}
            <p className={styles.muted}>
              {team.image_source === 'logo' && 'Team logo, shown in place of a country flag.'}
              {team.image_source === 'flag' && `Country flag (${team.country_code}). Upload a logo to replace it.`}
              {!team.image_source &&
                (team.country_code
                  ? `No flag for ${team.country_code}. Upload a logo for this team.`
                  : 'No image yet. Upload a logo, or set a country code above to use its flag.')}
            </p>
          </div>
          <ImageManagement entityType="cupTeam" entityId={teamId!} onChange={() => mutate()} />
        </Panel>
      </div>

      <Panel>
        <Panel.Header>
          <h2>Seasons</h2>
        </Panel.Header>
        {seasons.length === 0 ? (
          <p className={styles.muted}>This team has not entered any seasons yet.</p>
        ) : (
          <>
            <div className={`${listTableStyles.tableHeader} ${styles.seasonHeader}`}>
              <div>Season</div>
              <div>Competition</div>
              <div>Dates</div>
              <div>Players</div>
              <div>Ties</div>
              <div />
            </div>
            <div className={`${listTableStyles.tableBody} ${styles.seasonBody}`}>
              {seasons.map((s) => (
                <div
                  key={s.tc_season_id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSeasonId(s.tc_season_id)}
                  onKeyDown={(e) => e.key === 'Enter' && setSeasonId(s.tc_season_id)}
                  className={`${listTableStyles.tableRow} ${styles.seasonRow} ${s.tc_season_id === seasonId ? styles.seasonRowSelected : ''}`}
                >
                  <div className={listTableStyles.primaryText}>{s.season_name || '—'}</div>
                  <div className={listTableStyles.cellText}>{s.competition_name || '—'}</div>
                  <div className={listTableStyles.cellText}>
                    {s.start_date || '?'} – {s.end_date || '?'}
                  </div>
                  <div className={listTableStyles.cellText}>{s.roster_count}</div>
                  <div className={listTableStyles.cellText}>{s.tie_count}</div>
                  <div className={listTableStyles.cellActions}>
                    {s.added_by_editor && (
                      <span title={s.roster_count > 0 || s.tie_count > 0 ? 'Remove its players first.' : ''}>
                        <Button
                          size="small"
                          variant="secondary"
                          disabled={busy || s.roster_count > 0 || s.tie_count > 0}
                          onClick={(e) => {
                            e.stopPropagation()
                            leaveSeason(s)
                          }}
                        >
                          Remove
                        </Button>
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <div className={styles.toolbar}>
          <div className={styles.toolbarField}>
            <label htmlFor="cup-team-enter-season">Add to a season</label>
            <SeasonSelect
              value={seasonToEnter}
              onChange={(s) => setSeasonToEnter(s as Season | null)}
              placeholder="Search seasons, such as Laver Cup 2026..."
              isDisabled={busy}
            />
          </div>
          <Button onClick={enterSeason} disabled={busy || !seasonToEnter}>Add to season</Button>
        </div>
      </Panel>

      {selectedSeason && (
        <>
          <Panel>
            <Panel.Header>
              <h2>Roster: {selectedSeason.season_name}</h2>
            </Panel.Header>
            {roster.length === 0 ? (
              <p className={styles.muted}>
                {team.ext_competitor_id
                  ? 'No players yet. Players are added automatically when their rubbers are scheduled.'
                  : 'No players yet. Add them below.'}
              </p>
            ) : (
              <>
                <div className={`${listTableStyles.tableHeader} ${styles.rosterHeader}`}>
                  <div>Player</div>
                  <div>Country</div>
                  <div>Added by</div>
                  <div />
                </div>
                <div className={listTableStyles.tableBody}>
                  {roster.map((entry) => (
                    <div key={entry.player_id} className={`${listTableStyles.tableRow} ${styles.rosterRow}`}>
                      <div className={listTableStyles.primaryText}>
                        <Link to={`/players/${entry.player_id}`}>{entry.name || 'Unknown player'}</Link>
                      </div>
                      <div className={listTableStyles.cellText}>{entry.country_code || '—'}</div>
                      <div className={listTableStyles.cellText}>
                        <Badge variant={entry.created_by.includes('@') ? 'primary' : 'neutral'} size="small">
                          {addedByLabel(entry.created_by)}
                        </Badge>
                      </div>
                      <div className={listTableStyles.cellActions}>
                        <span title={entry.has_rubbers ? 'Played for the team this season.' : ''}>
                          <Button
                            size="small"
                            variant="secondary"
                            disabled={busy || entry.has_rubbers}
                            onClick={() => removePlayer(entry)}
                          >
                            Remove
                          </Button>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className={styles.toolbar}>
              <div className={styles.toolbarField}>
                <label htmlFor="cup-roster-add">Add a player</label>
                <CompetitorSelect
                  value={playerToAdd}
                  onChange={(c) => setPlayerToAdd(c as Competitor | null)}
                  teamType="singles"
                  placeholder="Search for a player..."
                  isDisabled={busy}
                />
              </div>
              <Button onClick={addPlayer} disabled={busy || !playerToAdd}>Add to roster</Button>
            </div>
          </Panel>

          <Panel>
            <Panel.Header>
              <h2>Ties: {selectedSeason.season_name}</h2>
            </Panel.Header>
            {ties.length === 0 ? (
              <p className={styles.muted}>No ties in this season yet.</p>
            ) : (
              <>
                <div className={`${listTableStyles.tableHeader} ${styles.tieHeader}`}>
                  <div>Date</div>
                  <div>Opponent</div>
                  <div>Round or group</div>
                  <div>Score</div>
                  <div>Result</div>
                  <div />
                </div>
                <div className={listTableStyles.tableBody}>
                  {ties.map((t) => (
                    <div key={t.tc_match_id} className={`${listTableStyles.tableRow} ${styles.tieRow}`}>
                      <div className={listTableStyles.cellText}>{t.start_time?.slice(0, 10) || '—'}</div>
                      <div className={listTableStyles.primaryText}>
                        {t.opponent_id ? <Link to={`/cup-teams/${t.opponent_id}`}>{t.opponent_name}</Link> : '—'}
                      </div>
                      <div className={listTableStyles.cellText}>{roundLabel(t.round) || t.group_name || '—'}</div>
                      <div className={listTableStyles.cellText}>{teamScore(t)}</div>
                      <div className={listTableStyles.cellText}>
                        {t.won === null ? (
                          <Badge variant="neutral" size="small">{t.status || '—'}</Badge>
                        ) : (
                          <Badge variant={t.won ? 'success' : 'error'} size="small">{t.won ? 'Won' : 'Lost'}</Badge>
                        )}
                      </div>
                      <div className={listTableStyles.cellActions}>
                        <Link to={`/ties/${t.tc_match_id}`}>
                          <Button size="small">View tie</Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            {showNewTie ? (
              <div className={styles.newTie}>
                <div className={formStyles.fieldGroup}>
                  <label htmlFor="new-tie-opponent">Opponent</label>
                  <CupTeamSelect
                    inputId="new-tie-opponent"
                    value={tieOpponent}
                    onChange={setTieOpponent}
                    excludeId={teamId}
                    placeholder="Search for a cup team..."
                    isDisabled={busy}
                  />
                </div>
                <div className={formStyles.fieldGroup}>
                  <label htmlFor="new-tie-side">{cupTeamName(team)} plays as</label>
                  <select
                    id="new-tie-side"
                    value={tieSide}
                    onChange={(e) => setTieSide(e.target.value as 'home' | 'away')}
                    disabled={busy}
                  >
                    <option value="home">Home</option>
                    <option value="away">Away</option>
                  </select>
                </div>
                <div className={formStyles.fieldGroup}>
                  <label htmlFor="new-tie-start">Start time (PT)</label>
                  <input
                    id="new-tie-start"
                    type="datetime-local"
                    value={tieStart}
                    onChange={(e) => setTieStart(e.target.value)}
                    disabled={busy}
                  />
                </div>
                <div className={formStyles.fieldGroup}>
                  <label htmlFor="new-tie-round">Round (optional)</label>
                  <input
                    id="new-tie-round"
                    type="text"
                    value={tieRound}
                    onChange={(e) => setTieRound(e.target.value)}
                    placeholder="final"
                    disabled={busy}
                  />
                </div>
                <div className={styles.toolbar}>
                  <Button onClick={createTie} disabled={busy || !tieOpponent}>Create tie</Button>
                  <Button variant="secondary" onClick={() => setShowNewTie(false)} disabled={busy}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className={styles.toolbar}>
                <Button onClick={() => setShowNewTie(true)} disabled={busy}>+ New tie</Button>
              </div>
            )}
          </Panel>
        </>
      )}
    </div>
  )
}

export default CupTeamDetail
